import React, { Fragment, useEffect, useState } from 'react';
import AdminPageLoader from './AdminPageLoader';
import axios from 'axios';
import { FaPlus, FaSearch, FaEdit, FaSpinner, FaTruck } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function DelieveryPartnerPage() {
    const [partnerList, setPartnerList] = useState([]);
    const [filteredPartners, setFilteredPartners] = useState([]);
    const [partnerData, setPartnerData] = useState({ name: '', phoneNumber: '' });
    const [savingPartner, setSavingPartner] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        handlePartnerList();
    }, []);

    // Lock body scroll when modal open
    useEffect(() => {
        if (showModal) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = originalOverflow; };
        }
    }, [showModal]);

    // Fetch all delivery partners
    const handlePartnerList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/delivery-partners`);
            const partners = response.data || [];
            setPartnerList(partners);
            setFilteredPartners(partners);
        } catch (error) {
            alert("Failed to fetch delivery partners. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Search partner with debouncing
    const handleSearchPartner = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = partnerList.filter(partner => 
                partner.name.toLowerCase().includes(search) ||
                partner.phoneNumber.toLowerCase().includes(search)
            );
            setFilteredPartners(filtered);
        } else {
            setFilteredPartners(partnerList);
        }
    };

    // Handle form input changes
    const handleChange = (event) => {
        setPartnerData({ ...partnerData, [event.target.name]: event.target.value });
    };

    // Validate input fields
    const validateFields = () => {
        if (!partnerData.name.trim()) {
            alert("Partner name is required.");
            return false;
        }
        if (!partnerData.phoneNumber.trim()) {
            alert("Phone number is required.");
            return false;
        }
        // Basic phone number validation
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(partnerData.phoneNumber.replace(/\s/g, ''))) {
            alert("Please enter a valid 10-digit phone number.");
            return false;
        }
        return true;
    };

    // Add or Update delivery partner
    const handleSavePartner = async () => {
        if (!validateFields()) return;

        try {
            setSavingPartner(true);
            if (editMode) {
                // Update existing partner
                await axios.put(`${API_BASE_URL}/delivery-partners/${editId}`, partnerData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert("Delivery partner updated successfully!");
            } else {
                // Add new partner
                await axios.post(`${API_BASE_URL}/delivery-partners`, partnerData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert("Delivery partner added successfully!");
            }
            handlePartnerList();
            resetForm();
            setShowModal(false);
        } catch (error) {
            alert(`Failed to ${editMode ? 'update' : 'add'} delivery partner. Please try again.`);
        } finally {
            setSavingPartner(false);
        }
    };

    // Open edit modal
    const handleEditPartner = (partner) => {
        setEditMode(true);
        setEditId(partner._id);
        setPartnerData({ name: partner.name, phoneNumber: partner.phoneNumber });
        setShowModal(true);
    };

    // Delete a delivery partner
    const handleDeletePartner = async (id) => {
        if (!window.confirm("Are you sure you want to delete this delivery partner?")) return;

        try {
            await axios.delete(`${API_BASE_URL}/delivery-partners/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Delivery partner deleted successfully!");
            handlePartnerList();
        } catch (error) {
            alert("Failed to delete delivery partner. Please try again.");
        }
    };

    // Reset form
    const resetForm = () => {
        setPartnerData({ name: '', phoneNumber: '' });
        setEditMode(false);
        setEditId(null);
    };

    // Handle modal close
    const handleModalClose = () => {
        resetForm();
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Heading */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">
                                    DELIVERY PARTNER MANAGEMENT ({partnerList.length} TOTAL)
                                </h4>
                                <p className="text-muted p-0 m-0">Manage your delivery partners and their contact information</p>
                            </div>
                            <button 
                                type="button" 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                            >
                                <FaPlus size={11} />
                                Add Delivery Partner
                            </button>
                        </div>
                    </div>
                </div>

                {/* Control Panel — cloned from Brands page */}
                <style>{`
                    .custom-dp-control-bar {
                        padding-top: 4px !important;
                    }
                `}</style>
                <div className="control-panel-bar mt-2 custom-dp-control-bar">
                    <div className="control-panel-search">
                        <span
                            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
                            style={{ pointerEvents: 'none' }}
                        >
                            <FaSearch style={{ fontSize: '14px' }} />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-5"
                            placeholder="Search by name or phone number..."
                            style={{
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                height: '38px',
                                fontSize: '14px',
                                background: '#ffffff',
                                width: '100%'
                            }}
                            value={searchTerm}
                            onChange={handleSearchPartner}
                        />
                    </div>
                    <div className="control-panel-reset" style={{ marginLeft: 'auto' }}>
                        <small className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                            Showing {filteredPartners.length} of {partnerList.length} delivery partners
                        </small>
                    </div>
                </div>

                {loading ? (
                    <AdminPageLoader message="Loading delivery partners..." />
                ) : (
                    <div className="brands-grid-wrap mt-4">
                        {filteredPartners.length > 0 ? (
                            filteredPartners.map((data) => (
                                <div className="brands-grid-item" key={data._id}>
                                    <div className="brand-flat-card">
                                        <div className="brand-flat-img-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', aspectRatio: 'auto', height: '100px' }}>
                                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#e8eaf6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a237e' }}>
                                                <FaTruck size={26} />
                                            </div>
                                        </div>
                                        <div className="brand-flat-body">
                                            <span className="brand-flat-name" style={{ fontWeight: '700', color: '#1a237e', fontSize: '14px' }}>
                                                {data.name}
                                            </span>
                                            <span className="text-muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                                                {data.phoneNumber}
                                            </span>
                                            <div className="brand-flat-actions">
                                                <button
                                                    onClick={() => handleEditPartner(data)}
                                                    className="brand-action-btn"
                                                    title="Edit Partner"
                                                    style={{ border: 'none', background: 'none', padding: 0 }}
                                                >
                                                    <FaEdit size={13} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePartner(data._id)}
                                                    className="brand-action-btn brand-action-btn--danger"
                                                    title="Delete Partner"
                                                    style={{ border: 'none', background: 'none', padding: 0 }}
                                                >
                                                    <MdDelete size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ width: '100%' }}>
                                <div className="card border-0 shadow-sm rounded-0">
                                    <div className="card-body text-center py-5">
                                        <FaTruck className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No delivery partners found matching your search.' : 'No delivery partners found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first delivery partner to get started.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Custom Overlay Modal */}
            {showModal && (
                <div className="assign-modal-overlay" onClick={() => { handleModalClose(); setShowModal(false); }}>
                    <div 
                        className="assign-modal-content" 
                        style={{ width: '450px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} 
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaTruck />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        {editMode ? 'Edit Delivery Partner' : 'Add New Delivery Partner'}
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                        Configure delivery partner profile details
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => { handleModalClose(); setShowModal(false); }}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div className="assign-modal-body" style={{ padding: '20px' }}>
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', display: 'block' }}>Partner Name</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', height: '38px', padding: '8px 12px' }}
                                    placeholder="Enter partner name" 
                                    name="name" 
                                    value={partnerData.name} 
                                    onChange={handleChange} 
                                />
                            </div>
                            <div className="mb-1">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', display: 'block' }}>Phone Number</label>
                                <input 
                                    type="tel" 
                                    className="form-control" 
                                    style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', height: '38px', padding: '8px 12px' }}
                                    placeholder="Enter 10-digit phone number" 
                                    name="phoneNumber" 
                                    value={partnerData.phoneNumber} 
                                    onChange={handleChange}
                                    maxLength="10"
                                />
                                <small style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', display: 'block' }}>Enter 10-digit phone number without country code</small>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="assign-modal-footer">
                            <button 
                                type="button" 
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: '1px solid #e2e8f0', background: '#ffffff',
                                    color: '#374151', fontSize: '13px', fontWeight: '500',
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }} 
                                onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                onClick={() => { handleModalClose(); setShowModal(false); }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: 'none', background: savingPartner ? '#c7d2fe' : '#1a237e',
                                    color: savingPartner ? '#6366f1' : '#ffff01',
                                    fontSize: '13px', fontWeight: '600',
                                    cursor: savingPartner ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px'
                                }} 
                                onClick={handleSavePartner} 
                                disabled={savingPartner}
                            >
                                {savingPartner ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Saving...
                                    </>
                                ) : (
                                    editMode ? 'Update Partner' : 'Save Partner'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default DelieveryPartnerPage;